// import Card from 'common/components/Card/Card';
import EditHeader from "common/components/EditHeader/EditHeader";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import { AccessRequest } from "types/AccessRequest";
import Status from "common/components/Status/Status";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import Button from "common/components/Button/Button";
import { checkEmail, checkPhoneFormat } from "common/utils/fieldsValidators";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import CheckBoxBlockItem from "common/components/CheckBoxBlockItem/CheckBoxBlockItem";
import EmptyResult from "common/components/EmptyResult/EmptyResult";
import useList from "common/hooks/useList";
import { Contact } from "types";
import WarningIcon from "components/Icon/icons/WarningIcon";
import { Organization } from "types/Organization";
import { VscTriangleRight } from "react-icons/vsc";

function AccessRequestForm({
  onSubmit,
  item,
}: {
  onSubmit: any;
  item: AccessRequest;
}) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();

  const { items: relatedContacts } = useList<Contact>("contacts", {
    defaultFilters: {
      user: { id: item?.user?.id },
    },
    populate: ["contactGroups"],
    cachePrefix: `AccessRequestForm_Contacts_all`,
  });

  const initialValues = useMemo(
    () =>
      item?.id
        ? {
            ...item,
            name: item?.user?.name,
            quality: relatedContacts?.[0]?.quality ?? item?.quality,
            email: item?.user?.email,
            address: item?.user?.geojson?.label,
            phoneNumber: item?.user?.phoneNumber,
            notifyMe: true,
            contactGroups: [], // no use populating it : no edit allowed.
            // overload avec les données du contact lié si existant
            ...(relatedContacts?.[0]
              ? {
                  ...relatedContacts?.[0],
                  contactGroups:
                    relatedContacts[0].contactGroups?.map((cg: any) => cg.id) ||
                    [],
                }
              : {}),
          }
        : {},
    [item, relatedContacts]
  );

  const onSubmitWrapper = async (values: any) => {
    const payload = { ...values };
    delete payload.user;
    delete payload.id;
    const res = await onSubmit(payload);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  const { items: organizations } = useList<Organization>("organizations", {
    populate: ["allowedGroups"],
  });

  const contactGroupsListByOrga = useMemo(() => {
    return (organizations || []).map((o: any) => ({
      organizationId: o.id,
      organizationName: o.name,
      contactGroups: (o.allowedGroups || []).map((ocg: any) => ({
        id: ocg.id,
        value: ocg.id,
        title: ocg.name,
      })),
    }));
  }, [organizations]);

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{
        values: true,
        submitting: true,
        pristine: true,
        touched: true,
        errors: true,
      }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      validate={(values) => {
        const errors: any = {};
        if (!values.name) {
          errors.name = t("common.fieldRequired");
        }
        if (!values.quality) {
          errors.quality = t("common.fieldRequired");
        }
        if (!values.email) {
          errors.email = t("common.fieldRequired");
        }
        if (!values.contactGroups?.length) {
          errors.contactGroups = t("forms.selectAtLeastOneGroup");
        }
        return errors;
      }}
      render={({ handleSubmit, errors, submitFailed }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t("common.acceptAccessRequest")}
              subtitle={item.user?.name}
              icon="BuildingOfficeIcon"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <div className="hidden md:flex md:flex-row lg:flex-row md:justify-end flex-wrap gap-2 lg:items-center mt-2 lg:mt-0">
                  <Button
                    title={t("actions.cancel")}
                    type="secondary"
                    icon="XCircleIcon"
                    onClick={() => navigate("/access-requests")}
                  />
                  <Button
                    title={t("common.validate")}
                    type="primary"
                    icon="CheckCircleIcon"
                    onClick={handleSubmit}
                  />
                </div>
              }
              status={
                <Status
                  status={item?.state}
                  label={t(
                    `common.${
                      item?.state === "accepted"
                        ? "stateApproved"
                        : item?.state === "pending"
                        ? "stateWaitingForApproval"
                        : "stateRefused"
                    }`
                  )}
                  small
                />
              }
            />

            {relatedContacts?.length ? (
              <div className="mx-1 rounded-sm mb-4 -mt-2 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="text-sm text-yellow-800 font-bold flex flex-row items-center gap-2">
                  <WarningIcon className="h-4 w-4" />
                  Contact existant - Attention aux modifications effectuées.
                </p>
              </div>
            ) : null}

            {submitFailed && Object.keys(errors || {}).length ? (
              <div className="mx-1 rounded-sm mb-4 -mt-2 bg-red-50 border-l-4 border-red-400 p-4">
                <p className="text-sm text-red-800 font-bold flex flex-row items-center gap-2">
                  <WarningIcon className="h-4 w-4" />
                  Veuillez compléter tous les champs nécessaires.
                </p>
              </div>
            ) : null}

            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex-lg gap-2 mt-2 w-full">
                <div className="flex flex-row gap-4 white-box w-full lg:flex-1">
                  <div className="flex flex-col w-full gap-2">
                    <SectionTitle
                      title={t("forms.requestContact")}
                      className="text-lg normal-case mb-2"
                    />
                    <FormControl
                      name="name"
                      title={t("forms.contactName")}
                      required
                    />
                    <FormControl
                      name="quality"
                      title={t("forms.quality")}
                      required
                    />

                    <FormControl
                      name="notes"
                      titleKey={t("forms.notes")}
                      type="textarea"
                      inputContainerStyle="flex-1"
                      containerStyle="flex w-full mb-4"
                      className="text-input"
                    />
                  </div>
                </div>
                <div className="flex flex-row gap-4 white-box w-full lg:flex-1">
                  <div className="flex flex-col gap-2 w-full">
                    <SectionTitle
                      title={t("forms.coordinates")}
                      className=" text-lg normal-case mb-2"
                    />
                    <FormControl name="address" titleKey={t("forms.address")} />

                    <FormControl
                      name="email"
                      titleKey={t("forms.email")}
                      validate={(val: string) => checkEmail(val, t, false)}
                      required
                      disabled
                    />

                    <FormControl
                      name="phoneNumber"
                      titleKey={t("forms.phoneNumber")}
                      validate={(val: string) =>
                        !val ? undefined : checkPhoneFormat(val, t)
                      }
                    />

                    <div className="flex items-center gap-4 mt-6">
                      <FormControl type="custom" name="notifyMe">
                        <ToggleSwitch />
                      </FormControl>
                      {t("forms.notifyMe")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-row gap-4 white-box w-full lg:flex-1 mt-4">
                <div className="flex flex-col w-full gap-2">
                  <SectionTitle
                    title={t("forms.requestContactGroupSelect")}
                    className="text-lg normal-case mb-2"
                  />
                  {contactGroupsListByOrga?.length ? (
                    <div className="flex flex-col gap-3 w-full">
                      {contactGroupsListByOrga.map((contactGroupsByOrga) => (
                        <div
                          className="flex flex-col gap-1 w-full"
                          key={contactGroupsByOrga.organizationId}
                        >
                          <p className="text-lg text-slate-500">
                            <VscTriangleRight className="h-4 w-4 inline-block" />
                            Organisation{" "}
                            <span className="font-semibold">
                              {contactGroupsByOrga.organizationName}
                            </span>
                          </p>
                          <div className="flex flex-col ml-4 w-full pl-5 pb-1 border-l-2 border-l-slate-200">
                            {contactGroupsByOrga.contactGroups?.length ? (
                              <FormControl
                                type="custom"
                                name="contactGroups"
                                inputContainerStyle="w-full"
                                containerStyle="w-full"
                                required
                              >
                                <RadioBlock
                                  Component={CheckBoxBlockItem}
                                  items={contactGroupsByOrga.contactGroups}
                                  multiple
                                  horizontal
                                />
                              </FormControl>
                            ) : (
                              <EmptyResult text={t("forms.noContactGroup")} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <SectionTitle title={t("forms.recipients")} />
                      <EmptyResult text={t("forms.noContactGroup")} />
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        );
      }}
    />
  );
}

export default AccessRequestForm;
